// /pricing plan cards — the expandable "what's actually in this plan" list.
//
// WHY THIS EXISTS. A card's bullets are a CURATED highlight reel (5-7 lines, CMS-edited prose) —
// deliberately not the full module list, because nobody reads a 30-line card. But a school
// comparing Growth against Pro needs to be able to see the ACTUAL module set behind "53 of 58
// modules" without leaving the card or cross-referencing the big table below by eye. This is
// that list, folded into the card, opened by the same chip that already states the count.
//
// IT DOES NOT INVENT A SECOND DATA SOURCE. Every label, group and grant here comes from
// `buildPricingCatalogue()` — the exact function `PricingFeatureCatalogue.js`'s big comparison
// table is built from — fed the exact resolvers that table exports (`resolveCoreGroups`,
// `resolveFeatureGroups`, `resolveCoreModuleLabels`). A platform-admin edit at
// `/platform/entitlements` (which moves a feature to a different tier) or a CMS edit to a
// group's label therefore reaches this list the SAME way it already reaches the table below —
// there is exactly one place a module's name, group and grant are decided.
//
// THE PANEL IS A TINT OF THE CARD, NOT A SECOND PRICE TAG. The per-student figure above it is a
// solid INVERTED callout — that treatment exists to make one number impossible to miss, and
// reusing it here would put two loud, competing inverted blocks in one card. This uses the same
// translucent-overlay language the identity chip at the top of the card already wears
// (`skin.chip`/`skin.rule`), so it reads as an inset ON the card, in the card's own ink — a
// reference panel, not a second headline.
import React from "react";
import { Check, ChevronDown, Clock } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Filter an already-built catalogue down to what ONE plan actually grants.
 *
 * `coreGroups` need no filtering — every core module is on every plan by definition — but
 * `featureGroups` are the FULL catalogue across all five plans, so each is filtered to this
 * plan's own `features[id] === true` and an emptied group is dropped rather than rendered with
 * a heading over nothing.
 */
export function planModulesForCard(catalogue, matrixPlans, planId) {
  const plan = (Array.isArray(matrixPlans) ? matrixPlans : []).find((p) => p?.id === planId);
  const granted = plan?.features && typeof plan.features === "object" ? plan.features : {};
  const featureGroups = (catalogue?.featureGroups || [])
    .map((group) => ({ ...group, items: (group.items || []).filter((item) => granted[item.id] === true) }))
    .filter((group) => group.items.length > 0);
  return { coreGroups: catalogue?.coreGroups || [], featureGroups };
}

/**
 * The toggle chip. Same visual chip the card already rendered ("N of 58 modules"), now a real
 * disclosure button — `aria-expanded` + `aria-controls` so it reads correctly to assistive tech,
 * and a chevron that rotates rather than a second icon appearing/disappearing.
 */
export function PlanModulesToggle({ planId, included, total, expanded, onToggle, skin }) {
  if (!included || !total) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={`plan-modules-list-${planId}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-bold tabular-nums",
        "transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-current",
        skin.meta,
      )}
      data-testid={`plan-modules-${planId}`}
    >
      {included} of {total} modules
      <ChevronDown
        className={cn("h-3 w-3 shrink-0 transition-transform duration-fast", expanded && "rotate-180")}
        strokeWidth={2.6}
        aria-hidden="true"
      />
    </button>
  );
}

function ModuleRow({ item }) {
  return (
    <li className="flex items-start gap-2 text-[12.5px] leading-snug" data-module-id={item.id}>
      {item.previewOnly ? (
        <Clock className="mt-[3px] h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
      ) : (
        <Check className="mt-[3px] h-3 w-3 shrink-0" strokeWidth={2.8} aria-hidden="true" />
      )}
      <span>
        {item.label}
        {item.previewOnly ? <span className="opacity-70"> (coming soon)</span> : null}
      </span>
    </li>
  );
}

function ModuleGroup({ group, faintClass }) {
  return (
    <div>
      <p className={cn("text-[10px] font-bold uppercase tracking-[0.11em]", faintClass)}>{group.label}</p>
      {/* ONE column, not two: this panel lives inside a ~260px card, not the wide four-column
          grid the same groups render in below the fold. A two-column attempt collided checkmarks
          into the next label the moment either wrapped ("Communication" running into "Class
          chat") — measured on the live page, not assumed. */}
      <ul className="mt-1.5 space-y-1">
        {group.items.map((item) => <ModuleRow item={item} key={item.id} />)}
      </ul>
    </div>
  );
}

/**
 * The expandable panel itself. Bounded height with an internal scroll — Enterprise legitimately
 * grants all 58 modules, and letting the panel grow unbounded would make one card's disclosure
 * blow the whole row's height past a comfortable viewport. Mounted only while `expanded` (plain
 * conditional render, not CSS visibility), so a screen reader never has to skip hidden content
 * and there is nothing here that can animate open onto an ambient loop.
 */
export function PlanModulesList({ planId, coreGroups, featureGroups, skin }) {
  return (
    <div
      id={`plan-modules-list-${planId}`}
      className={cn(
        "mt-3 max-h-[22rem] overflow-y-auto rounded-2xl p-3.5",
        skin.chip,
      )}
      role="region"
      aria-label="Every module included in this plan"
      tabIndex={0}
      data-testid={`plan-modules-list-${planId}`}
    >
      <div className={cn("space-y-3.5", skin.sub)}>
        {coreGroups.map((group) => (
          <ModuleGroup group={group} faintClass={skin.faint} key={`core-${group.id}`} />
        ))}
      </div>
      {featureGroups.length > 0 ? (
        <div className={cn("mt-4 space-y-3.5 border-t pt-3.5", skin.rule, skin.sub)}>
          {featureGroups.map((group) => (
            <ModuleGroup group={group} faintClass={skin.faint} key={`feature-${group.id}`} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
